// Map-bundle schema. A "map" is a directory under src/maps/bundles/<id>/
// containing a `map.json` conforming to this schema, plus optional TS modules
// under behaviors/ and materials/.
//
// The format is deliberately flat and explicit. The editor reads and writes
// these shapes directly; the driver walks them at load time. Adding a new
// field is always a two-way decision: does the editor need to author it, and
// does the driver need to consume it?

export type Vec3 = readonly [number, number, number];
export type Vec2 = readonly [number, number];
export type ColorHex = string; // "#rrggbb"

/** Optional rotation in Euler angles (radians, XYZ order). */
export interface Transform {
  p?: Vec3;         // position, default [0,0,0]
  r?: Vec3;         // rotation (euler XYZ, radians), default [0,0,0]
  s?: Vec3 | number; // scale, default 1
}

// -- Materials ---------------------------------------------------------------
// Three forms, discriminated structurally:
//   1. Inline PBR            — plain color + roughness/metalness.
//   2. Preset reference      — names a built-in preset with tunable params.
//   3. Bundle module         — loads materials/<name>.ts from the bundle.

export interface MaterialInline {
  color?: ColorHex;
  roughness?: number;
  metalness?: number;
  emissive?: ColorHex;
  emissiveIntensity?: number;

  // -- Optional PBR texture maps (URLs; may be `asset://<id>`) -----
  // colour-space gets set automatically: SRGB for color/emissive,
  // linear (NoColorSpace) for normal/roughness/metalness/ao.
  /** Albedo / base colour map. */
  mapColor?: string;
  /** Tangent-space normal map. */
  mapNormal?: string;
  /** Greyscale roughness map. */
  mapRoughness?: string;
  /** Greyscale metalness map. */
  mapMetalness?: string;
  /** Ambient occlusion map (uses uv2 — auto-cloned from uv when missing). */
  mapAo?: string;
  /** Emissive colour map. */
  mapEmissive?: string;
  /** Greyscale heightmap used as a bumpMap — perturbs the shading normal
   *  via finite differences, no vertex displacement, no extra geometry. */
  mapBump?: string;
  /** UV tiling [u, v]. Default [1, 1]. Applied to every map slot. */
  uvScale?: Vec2;
  /** UV offset [u, v]. Default [0, 0]. */
  uvOffset?: Vec2;
  /** Normal-map intensity. Default 1. Larger = more pronounced relief. */
  normalScale?: number;
  /** Bump-map intensity. Default 1. Stacks on top of normalMap; the heightmap
   *  texture's gradient is added to the shading normal at no vertex cost. */
  bumpScale?: number;
  /** AO-map intensity. Default 1. */
  aoIntensity?: number;
}
export interface MaterialPreset {
  preset: string;
  params?: Record<string, unknown>;
}
export interface MaterialModule {
  module: string; // filename under bundle/materials/ (without .ts)
  params?: Record<string, unknown>;
}
export type MaterialRef = MaterialInline | MaterialPreset | MaterialModule;

export function isMaterialPreset(m: MaterialRef | undefined): m is MaterialPreset {
  return !!m && typeof (m as MaterialPreset).preset === 'string';
}
export function isMaterialModule(m: MaterialRef | undefined): m is MaterialModule {
  return !!m && typeof (m as MaterialModule).module === 'string';
}

// -- Colliders ---------------------------------------------------------------

export type BodyKind = 'none' | 'static' | 'dynamic' | 'kinematic';
export type ColliderKind =
  | 'auto'        // derive from the primitive's shape
  | 'box' | 'sphere' | 'cylinder' | 'capsule'
  | 'trimeshFromModel'; // modelRef-only: trimesh from GLB

export interface ColliderSpec {
  kind?: ColliderKind;    // default 'auto'
  body?: BodyKind;        // default 'static'
  friction?: number;
  restitution?: number;
  density?: number;
  gravityScale?: number;
  /** Membership bits within the ARENA collision group. Defaults to the
   *  standard arena bit so characters interact as expected. */
  membership?: number;
  filter?: number;
  /** Suffix id for per-bundle lookups. Behaviors resolve this via ctx.getBody. */
  // (The parent object already has `id`; this is just a convenience.)
}

// -- Primitives / objects ----------------------------------------------------

export interface BaseObject {
  id?: string;
  name?: string;
  transform?: Transform;
  material?: MaterialRef;
  collider?: ColliderSpec;
}

export interface GroupObject extends BaseObject {
  type: 'group';
  children?: ObjectNode[];
}
export interface BoxObject extends BaseObject {
  type: 'box';
  size: Vec3;
}
export interface SphereObject extends BaseObject {
  type: 'sphere';
  radius: number;
  /** widthSegments, heightSegments — defaults 32×16. */
  segments?: readonly [number, number];
}
export interface CylinderObject extends BaseObject {
  type: 'cylinder';
  radiusTop: number;
  radiusBottom: number;
  height: number;
  radialSegments?: number;
}
export interface CapsuleObject extends BaseObject {
  type: 'capsule';
  radius: number;
  halfHeight: number;
  /** 'y' (three default) | 'x' | 'z'. Affects mesh + collider orientation. */
  axis?: 'x' | 'y' | 'z';
}
export interface PlaneObject extends BaseObject {
  type: 'plane';
  size: readonly [number, number];
  segments?: readonly [number, number]; // widthSegs, heightSegs — default [1,1]
}
export interface CircleObject extends BaseObject {
  type: 'circle';
  radius: number;
  segments?: number;
}
export interface ModelRefObject extends BaseObject {
  type: 'modelRef';
  src: string;                    // absolute path (public-served) or assets/...
  /** If set, uniformly scale the loaded model so its longest bbox dim matches. */
  scaleToMaxDim?: number;
  /** Degrees applied as a Y rotation to the loaded model. */
  yawDeg?: number;
}

export type ObjectNode =
  | GroupObject
  | BoxObject
  | SphereObject
  | CylinderObject
  | CapsuleObject
  | PlaneObject
  | CircleObject
  | ModelRefObject;

// -- Lights ------------------------------------------------------------------

export interface AmbientLightDef {
  type: 'ambient';
  id?: string;
  color?: ColorHex;
  intensity?: number;
}
export interface DirectionalLightDef {
  type: 'directional';
  id?: string;
  direction?: Vec3; // world-space direction TOWARD the light source
  color?: ColorHex;
  intensity?: number;
}
export interface PointLightDef {
  type: 'point';
  id?: string;
  position: Vec3;
  color?: ColorHex;
  intensity?: number;
  range?: number;
  decay?: number;
}
export type LightDef = AmbientLightDef | DirectionalLightDef | PointLightDef;

// -- Sky / Fog / Audio / Behaviors -------------------------------------------

export interface SkyDef {
  preset: string;                // e.g. 'sandboxSky' | 'space' | 'moonSky' | 'saturn'
  params?: Record<string, unknown>;
}

/** Per-map Image-Based Lighting override. When omitted, the renderer uses
 *  its CONFIG defaults ('roomDefault' RoomEnvironment at the configured
 *  intensity). Each field is optional — set only what differs from default.
 *  The renderer copies these onto CONFIG.render.iblXxx at init() before
 *  applying, so the debug panel can still tweak them live.
 *
 *    'roomDefault' — three.js RoomEnvironment, no rotation.
 *    'roomAligned' — RoomEnvironment yawed to agree with the map's sunDir.
 *    'hemisphere'  — IBL off, replaced by a HemisphereLight gradient.
 *    'hdr'         — equirectangular HDR loaded from `hdrPath`.
 */
export interface IblDef {
  mode?: 'roomDefault' | 'roomAligned' | 'hemisphere' | 'hdr';
  intensity?: number;
  rotationDeg?: number;
  hemiSky?: ColorHex;
  hemiGround?: ColorHex;
  hemiIntensity?: number;
  hdrPath?: string;
}

export interface FogDef {
  preset: string;
  params?: Record<string, unknown>;
}

/** Environmental reverb — shared convolver tail driven by any SFX that
 *  requests a wet send. Omit the field entirely to disable reverb on the
 *  map. All sub-fields are optional with sensible defaults so authors can
 *  just write `"reverb": {}` to get the built-in settings. */
export interface ReverbDef {
  /** Optional URL to an audio file used as the convolver's impulse response.
   *  When set, the file's decoded buffer drives the reverb tail and the
   *  procedural fields below are ignored. Falls back to the generated IR if
   *  loading fails. */
  irUrl?: string;
  /** Global wet multiplier (0..). Scales every source's reverb send at once,
   *  on top of each source's own `wetGain`. Default 1.0. */
  wet?: number;
  /** Seconds of convolution tail (generated IR only). Default 4.0. */
  durationSec?: number;
  /** Decay exponent on the impulse response — higher = faster tail
   *  (generated IR only). Default 2.5. */
  decay?: number;
  /** Peak amplitude of the IR pre-decay (generated IR only). Default 0.55. */
  amplitude?: number;
}

/** Character footstep SFX. When present, each character's foot-plant event
 *  plays a random sample from `urls` at the foot's world position. Absent
 *  → no footstep audio. */
export interface FootstepsDef {
  urls: string[];
  /** Optional alternate sample set used when the foot's world Y is below
   *  `waterY`. Lets a beach / boat map swap "wood" thuds for "splash"
   *  samples without code. Ignored when waterUrls is absent. */
  waterUrls?: string[];
  /** World-Y threshold below which footsteps switch to `waterUrls`. The
   *  map's actual water surface (set by the ocean behavior) often differs
   *  from the gameplay-feel "splash" threshold by a small margin, so this
   *  knob is exposed independently. */
  waterY?: number;
  /** Dry gain (0..). Default 2.7. */
  volume?: number;
  /** Reverb send (0..1). Ignored when the map has no reverb. Default 0.15. */
  reverbWet?: number;
}

/** Splash SFX played when a character enters / paddles through water. The
 *  sim emits one event on the rising edge of the swim state (gated on
 *  downward pelvis velocity so calm wade-ins stay silent) plus debounced
 *  stroke pulses while the player is moving in water. Round-robin sample
 *  selection — same pool pattern as `footsteps`. Absent → silent splashes. */
export interface SwimSplashDef {
  urls: string[];
  /** Dry gain (0..). Default 1.0. */
  volume?: number;
  /** Reverb send (0..1). Ignored when the map has no reverb. Default 0.2. */
  reverbWet?: number;
}

/** Non-positional looping background sound. Typical use: ocean ambience,
 *  ship creaking, rain. Plays for the duration of the round, not tied to
 *  any in-world position — same routing as the music bus (no panner, no
 *  reverb send). */
export interface AmbientLoopDef {
  url: string;
  /** Linear gain (0..1). Default 0.5. */
  volume?: number;
}

/** Looping sound whose volume is driven by a behavior's `sinkProgress`
 *  (or another 0..1 progress accessor). Used by the sinking-ship map to
 *  ramp `shipsinking.wav` from silent to full as the deck descends. */
export interface SinkLoopDef {
  url: string;
  /** Behavior id whose `sinkProgress` getter (0..1) drives the gain.
   *  Default 'sink'. */
  behaviorId?: string;
  /** Peak gain at progress=1. Default 1.0. */
  volume?: number;
  /** Progress at which the loop reaches `volume`. Lets the user say
   *  "full volume by half submersion" without changing the behavior's
   *  curve. Default 0.5. */
  fullAtProgress?: number;
}

export interface AudioDef {
  /** Background music URL (looped). */
  music?: string;
  /** Music volume override — otherwise uses CONFIG.audio.musicVolume. */
  musicVolume?: number;
  /** Environmental reverb config; absent = dry map. */
  reverb?: ReverbDef;
  /** Character footsteps; absent = silent feet. */
  footsteps?: FootstepsDef;
  /** Splash SFX for the swimming mechanic; absent = silent splashes. */
  swimSplash?: SwimSplashDef;
  /** Non-positional ambient loops (ocean, ship-creak, rain, etc.). */
  ambientLoops?: AmbientLoopDef[];
  /** Optional progress-driven loop (sinking-ship crescendo). */
  sinkLoop?: SinkLoopDef;
  /** Optional looped bed that starts when the follow-cam dips below the
   *  map's `waterY` and stops when it surfaces. Same shape as a regular
   *  ambient loop. Absent → no underwater bed (the lowpass + post effect
   *  still run; only the loop is gated on a URL being present). */
  underwaterLoop?: AmbientLoopDef;
  /** Per-SFX reverb wet sends (0..1). Applied on top of the source's built-in
   *  default. Only meaningful when `reverb` is set. */
  hitReverbWet?: number;
  swingReverbWet?: number;
}

export interface BehaviorDef {
  id: string;
  module: string; // filename under bundle/behaviors/ (without .ts)
  params?: Record<string, unknown>;
}

// -- Map root ----------------------------------------------------------------

/** Layout metadata for the MapSelectScreen card. Each card self-describes its
 *  thumbnail + tilt + stage position so adding a map is a pure JSON change —
 *  no central lookup table to keep in sync. Stage coordinates are in the
 *  1920×1080 design space the MapSelectScreen scales from. */
export interface PickerCardDef {
  thumbnail: string;
  rotationDeg: number;
  left: number;
  top: number;
}

export interface MapJSON {
  /** Stable id — usually matches the bundle directory name. */
  id: string;
  label: string;
  description: string;
  loadingHint?: string;
  /** When true, the map is omitted from MapSelectScreen. Dev/debug maps (e.g.
   *  sandbox) use this; playable maps leave it unset. */
  hiddenFromPicker?: boolean;
  /** When true, the map is omitted from the ONLINE map vote. Use this for
   *  maps that depend on render-side assets (e.g. GLB models) that don't
   *  load on a Node server. Local play is unaffected. Defaults to false. */
  hiddenFromOnline?: boolean;
  /** Picker card layout. Required for any map that appears in
   *  MapSelectScreen (i.e. anywhere hiddenFromPicker isn't true). */
  pickerCard?: PickerCardDef;

  properties?: {
    gravity?: number;        // default CONFIG.sim.gravity
    simSpeed?: number;       // default 1
    killY?: number;
    /** Advisory radius for consumers (AI path planner, camera clamp).
     *  Square rings report their inscribed-circle radius (= halfSize) here.
     *  Defaults to the radius of the first cylinder primitive at the origin,
     *  then to 7.0. */
    radius?: number;
    /** World-space direction toward the key light (sun). Renderer aligns its
     *  default directional light to this so toon shading agrees with the
     *  sky / rock shaders. Omit to keep the Renderer's default direction. */
    sunDir?: Vec3;
    /** Water-surface world Y. When present the sim runs a per-tick submersion
     *  check against this plane and switches characters whose body is more
     *  than half-submerged into swimming mode (buoyancy + drag + arm flail,
     *  punch / headbutt suppressed). Absent → no swim mechanic; existing
     *  dry maps are unaffected. */
    waterY?: number;
  };

  sky?: SkyDef;
  fogs?: FogDef[];
  /** Image-Based Lighting override. Omit to use the renderer defaults. */
  ibl?: IblDef;
  lights?: LightDef[];
  audio?: AudioDef;

  objects?: ObjectNode[];
  behaviors?: BehaviorDef[];

  /** Optional per-map spawn points. If present, the World uses these in order
   *  (index = player index); otherwise it falls back to the default 2.0 m
   *  circle at origin. `p` is the XZ ground position; `yaw` defaults to
   *  facing the map centre (`atan2(-x, -z)`). `y` overrides the default
   *  pelvis spawn height (used by maps where players need to drop in from
   *  above an elevated platform — e.g. the sinking-ship deck). Maps with
   *  fewer entries than the match's player count fall through to the
   *  circle for overflow indices so mis-authored files don't crash. */
  spawns?: Array<{ p: readonly [number, number]; yaw?: number; y?: number }>;
}

/** Shallow validation. Throws a user-readable Error naming the bad field so
 *  authoring regressions surface immediately instead of blowing up deep in
 *  the driver. */
export function validateMap(raw: unknown): asserts raw is MapJSON {
  if (!raw || typeof raw !== 'object') {
    throw new Error('[MapJSON] map.json must be an object');
  }
  const m = raw as Partial<MapJSON>;
  if (typeof m.id !== 'string' || !m.id) {
    throw new Error('[MapJSON] missing required string field: id');
  }
  if (typeof m.label !== 'string') {
    throw new Error(`[MapJSON] ${m.id}: missing required string field: label`);
  }
  if (typeof m.description !== 'string') {
    throw new Error(`[MapJSON] ${m.id}: missing required string field: description`);
  }
  if (m.objects && !Array.isArray(m.objects)) {
    throw new Error(`[MapJSON] ${m.id}: objects must be an array`);
  }
  if (m.behaviors && !Array.isArray(m.behaviors)) {
    throw new Error(`[MapJSON] ${m.id}: behaviors must be an array`);
  }
  if (m.lights && !Array.isArray(m.lights)) {
    throw new Error(`[MapJSON] ${m.id}: lights must be an array`);
  }
}
